import React from "react";
import { LinkButton } from "@adsk/alloy-react-button";
import { NOTIFICATION_STATUSES } from "@adsk/alloy-react-notification";
import { StatusCodes } from "../utils/browser/consts";
import { useTranslations } from "@adsk/acc-issues-translations";
import { trackQRPReportCompleted } from "../utils/analytics/issueActionsTracking";
import { getReportsSdk } from "@adsk/acc-issues-core/report-service/ReportsSdk";

import { AccountResource, ReportCreationParams } from "@adsk/acs-reports-sdk";
import { ISSUES_SERVICE } from "@adsk/acc-issues-common/components/form/IssueExportPanelBody/constants";

const MessageWithRetryButton = ({
  reportTitle,
  onRetry,
}: {
  reportTitle: string;
  onRetry: () => void;
}) => {
  const { T } = useTranslations();
  return (
    <div>
      <div style={{ marginBottom: 6 }}>
        {T.translate("reports.failureMessage", { reportTitle })}
      </div>
      <LinkButton onClick={onRetry}>{T.translate("tryAgain")}</LinkButton>
    </div>
  );
};

const ReportSuccessMessage = ({ email }: { email: string }) => {
  const { T } = useTranslations();
  return <>{T.translate("reports.emailBeingSent", { email })}</>;
};

const ReportGenericFailureMessage = ({
  reportTitle,
}: {
  reportTitle: string;
}) => {
  const { T } = useTranslations();
  return <>{T.translate("reports.genericFailureMessage", { reportTitle })}</>;
};

const ReportNoPermissionsFailureMessage = ({
  reportTitle,
}: {
  reportTitle: string;
}) => {
  const { T } = useTranslations();
  return (
    <>{T.translate("reports.noPermissionsFailureMessage", { reportTitle })}</>
  );
};

const ReportIsReadyMessage = ({ reportTitle }: { reportTitle: string }) => {
  const { T } = useTranslations();
  return (
    <>{T.translate("reports.reportIsReadyLinkWasSent", { reportTitle })}</>
  );
};

// TODO: this is duplicated from alloy but could be exported
// https://git.autodesk.com/BIM360/alloy-react/blob/master/packages/Notification/helpers/consts.ts#L9-L11
declare type NotificationStatuses = typeof NOTIFICATION_STATUSES;
declare type NotificationStatus =
  NotificationStatuses[keyof NotificationStatuses];

export const createAndPollIssueReport = async ({
  attributes,
  projectId,
  email,
  handleNotification,
  isDeletedSpace,
  showInfoNotification = false,
}: {
  attributes: Omit<ReportCreationParams, keyof AccountResource | "service">;
  projectId: string;
  email: string;
  handleNotification: (data: {
    status: NotificationStatus;
    message: React.ReactNode;
  }) => void;
  isDeletedSpace: boolean;
  showInfoNotification?: boolean;
}) => {
  const reportTitle = attributes.title!;
  let message = <ReportSuccessMessage email={email} />;
  let status: NotificationStatus = NOTIFICATION_STATUSES.INFO;
  let reportId = "",
    reportStatus;
  const onRetry = () =>
    createAndPollIssueReport({
      attributes,
      projectId,
      email,
      isDeletedSpace,
      handleNotification,
      showInfoNotification,
    });
  const reportsSdk = getReportsSdk();

  try {
    const response = await reportsSdk.createReport({
      ...attributes,
      service: ISSUES_SERVICE,
      projectId,
    } as ReportCreationParams);
    reportId = response.id!;
  } catch (e: any) {
    message = <ReportGenericFailureMessage reportTitle={reportTitle} />;
    status = NOTIFICATION_STATUSES.ERROR;
    // SDK v6 collapsed the per-operation error classes into
    // `FailedRequestError`, which carries the HTTP status as `statusCode`.
    switch (e.statusCode) {
      case StatusCodes.FORBIDDEN:
        message = (
          <ReportNoPermissionsFailureMessage reportTitle={reportTitle} />
        );
        break;
      case StatusCodes.SERVICE_UNAVAILABLE:
        message = (
          <MessageWithRetryButton reportTitle={reportTitle} onRetry={onRetry} />
        );
        break;
      default:
        break;
    }
  }

  if (showInfoNotification || status === NOTIFICATION_STATUSES.ERROR) {
    handleNotification({
      status,
      message,
    });
  }

  if (status !== NOTIFICATION_STATUSES.ERROR) {
    let response;
    try {
      response = await reportsSdk.pollReport({
        id: reportId,
        projectId,
        interval: 3000,
        timeout: Infinity,
      });
      reportStatus = response.status;
    } catch {
      reportStatus = "error";
    } finally {
      if (reportStatus === "error") {
        handleNotification({
          status: NOTIFICATION_STATUSES.ERROR,
          message: (
            <MessageWithRetryButton
              reportTitle={reportTitle}
              onRetry={onRetry}
            />
          ),
        });
      } else {
        handleNotification({
          status: NOTIFICATION_STATUSES.SUCCESS,
          message: <ReportIsReadyMessage reportTitle={reportTitle} />,
        });
        trackQRPReportCompleted({ isIssueDeleted: isDeletedSpace });
      }
      // SDK v6 replaced the single `url` with a `urls` object. `publicLink`
      // is the successor of the old `url`, but it is always null in GovCloud,
      // so fall back to the login-gated `link` -- the same precedence the
      // Quick Reports Panel v4 uses.
      return response?.urls?.publicLink ?? response?.urls?.link;
    }
  }
};
